import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiActions } from '../../../../../hooks/use-api-actions';
import { CategoriesResponse } from '../../../../../models/api/back/create-order/categories';
import { ProcessingDataEntity } from '../../../../../models/api/sift/back/api-processing';
import { ProductDataEntity } from '../../../../../models/api/sift/back/api-products';
import { PrintItemElementDataEntity } from '../../../../../models/api/sift/back/api-elements';
import { ItemSettingsDataEntity } from '../../../../../models/api/sift/back/api-item-settings';
import { ApiGetProcessing } from '../../../../../api/sift/back/api-processing';
import { ApiCategories } from '../../../../../api/back/create-order/categories/api-categories';
import { ApiGetProducts } from '../../../../../api/sift/back/api-products';
import { ApiGetElements } from '../../../../../api/sift/back/api-elements';
import { ApiGetItemSettings } from '../../../../../api/sift/back/api-item-settings';
import { ApiProductDetail } from '../../../../../api/back/create-order/product-detail/api-product-detail';
import { useErrorMessage } from '../../../../../hooks/use-error-message';
import { Env } from '../../../../../models/env';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { dialogActions } from '../../../../dialog/slice/dialog-slice';
import { push } from 'connected-react-router';
import { PathParams, RoutingPath } from '../../../../../routes/routing-path';
import { useParams } from 'react-router-dom';
import { useLoading } from '../../../../ui/loading/hooks/use-loading';
import { printActions } from '../../../../../slices/sift/print-slice';
import { usePrintOrder } from '../../hooks/use-print-order';
import { ProductDetailResponse } from '../../../../../models/api/back/create-order/product-detail';
import { PrintSizeChange } from '../../print-select-images/module/print-size-change';
import { apiActions } from '../../../../../slices/api-slice';
import { ApiGetTemplates } from '../../../../../api/sift/back/api-templates';
import { TemplatesResponse } from '../../../../../models/api/sift/back/api-templates';
import { XmlParser } from '../../../../../manager/xml-parser';
import { TrimTempModel } from '../../../../../trim-editor/model/trim.temp-model';
import { INIT_PRINT_SHOP_SETTING } from '../../../../../api/front/system-config/api-meta-shop';
import { usePrintLock } from '../../hooks/use-print-lock';
import { usePrintStatus } from '../../hooks/use-print-status';
import { PrintProductDataEntity, PrintTemplateDataEntity } from '../../../../../models/sift/json/print-model';
import { FavoriteBrandConfigForPrint } from '../../../../dialog/unique/favorite-brand-config-for-print';
import { ApiPrintFavoriteProductDataGet, PrintFavoriteProductData } from '../../../../../api/sift/front/api-print-favorite-product-data-get';
import { ResponseBase } from '../../../../../api/response-base';
import { ApiEndpaper } from '../../../../../api/back/create-order/endpaper/api-endpaper';
import { ApiEndpaper2 } from '../../../../../api/back/create-order/endpaper/api-endpaper2';
import { cloneDeep } from 'lodash';
import { Endpaper2Response, EndpaperResponse } from '../../../../../models/api/back/create-order/endpaper';

export type PrintOrderRequestParam = {
  paperType?: string,
  borderType?: string,
  laminateType?: string,
  layoutInitPos?: number,
  printDate?: boolean,
  layoutMargin?: boolean,
  trimming?: boolean,
  backPrint?: boolean,
  additionalInfo?: string,
  additionalInfo2?: string,
}

export const useCreatePrintOrder = () => {
  /* hooks */
  const dispatch = useAppDispatch();
  const lockActions = usePrintLock();
  const { kijshopCd, shopOrderId } = useParams<PathParams>();
  const { asyncApiRun } = useApiActions();
  const { pushMessage } = useErrorMessage();
  const { getData } = usePrintOrder();
  const loadingHooks = useLoading({});
  const {
    currentData,
    shopMeta,
    isNextRouteSelectImage,
  } = useAppSelector((state) => ({
    currentData: state.print.printShopOrderData[shopOrderId],
    shopMeta: state.systemConfig.metaData,
    isNextRouteSelectImage: state.print.isNextRouteSelectImage,
  }));

  const {
    changeStatus,
  } = usePrintStatus();
  /* ---- data ---- */
  /* 画面状態 */
  const [status, setStatus] = useState<'select' | 'info'>('select');
  const [isSizeAdd, setIsSizeAdd] = useState(false);
  /* カテゴリ */
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<CategoriesResponse['service']>([]);
  const categoryOptions = useMemo(() => {
    return categories.map((v) => ({ value: v.serviceId, label: v.serviceName }));
  }, [categories]);

  /* 商品グループ */
  const [processing, setProcessing] = useState('');
  const [processingList, setProcessingList] = useState<ProcessingDataEntity[]>([]);
  const processingOptions = useMemo(() => {
    const data = processingList.flat().map((v) => v.printProcessData).flat(2);
    return data.map((v) => ({ value: v.printProcessId, label: v.printProcessName ?? '' }));
  }, [processingList]);

  /* キーワード */
  const [word, setWord] = useState('');


  /* 商品情報 */
  const [products, setProducts] = useState<ProductDataEntity[]>([]);
  const [product, setProduct] = useState('');
  const [productDetail, setProductDetail] = useState< null | ProductDetailResponse>(null);
  const productItemDetail = useMemo(() => {
    return productDetail?.item[0];
  }, [productDetail]);

  /* 商品部材加工設定情報 */
  const [itemSetting, setItemSetting] = useState<null | ItemSettingsDataEntity>(null);
  /* 商品部材加工プルダウン情報 */
  const [itemElements, setItemElements] = useState<PrintItemElementDataEntity[]>([]);
  /* マットプルダウン情報 */
  const [additionalInfoList, setAdditionalInfoList] = useState<EndpaperResponse['additionalInfos']>([]);
  /* テープ色プルダウン情報 */
  const [additionalInfoList2, setAdditionalInfoList2] = useState<Endpaper2Response['additionalInfos']>([]);
  /* 部材加工サイズ選択 */
  const [paperType, setPaperType] = useState('');
  const [borderType, setBorderType] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [additionalInfo2, setAdditionalInfo2] = useState('');
  const [laminateType, setLaminateType] = useState('');
  const [layoutInitPos, setLayoutInitPos] = useState(1);
  const [layoutMargin, setLayoutMargin] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [backPrint, setBackPrint] = useState(false);
  const [printDate, setPrintDate] = useState(false);

  const finishPath = useMemo(() => {
    switch (true) {
      case isNextRouteSelectImage:
        return RoutingPath.cnv.printOrderSelectImage({ kijshopCd, shopOrderId });
      default:
        return RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId });
    }
  }, [
    kijshopCd,
    shopOrderId,
    isNextRouteSelectImage,
  ]);

  /* ---- callback ---- */
  const getSelectItem = useCallback(() => new Promise<{
    product: ProductDataEntity,
    productDetail: ProductDetailResponse,
    itemSetting: ItemSettingsDataEntity,
    itemElements: PrintItemElementDataEntity[],
  }>((resolve, reject) => {
    if (!product) {
      reject(['商品が選択されていません']);
      return;
    }
    const targetProduct = products.find((v) => v.productNameId === product);
    if (!targetProduct || !productDetail) {
      reject(['商品が選択されていません']);
      return;
    }
    if (!itemSetting) {
      reject(['商品情報を読み込み中です。']);
      return;
    }
    resolve({
      product: targetProduct,
      productDetail,
      itemSetting,
      itemElements,
    });
  }), [
    product, products,
    productDetail,
    itemSetting,
    itemElements,
  ]);
  const resetAdditional = useCallback(() => {
    setAdditionalInfo('');
    setAdditionalInfo2('');
    setAdditionalInfoList([]);
    setAdditionalInfoList2([]);
  }, []);
  const resetInfoData = useCallback(() => {
    setPaperType('');
    setBorderType('');
    setLaminateType('');
    setLayoutInitPos(shopMeta.printSetting?.layoutPos ?? INIT_PRINT_SHOP_SETTING.layoutPos);
    setLayoutMargin(shopMeta.printSetting?.layoutMargin ?? INIT_PRINT_SHOP_SETTING.layoutMargin);
    setTrimming(shopMeta.printSetting?.autoTrimming ?? INIT_PRINT_SHOP_SETTING.autoTrimming);
    setBackPrint(shopMeta.printSetting?.backPrint ?? INIT_PRINT_SHOP_SETTING.backPrint);
    setPrintDate(false);
    resetAdditional();
  }, [
    shopMeta,
    resetAdditional,
  ]);

  const prepareProcessingData = useCallback((v: string) => {
    setCategory(v);
    setProcessing('');
    setProduct('')
    setProcessingList([]);
    setProducts([]);
    setItemSetting(null);
    resetAdditional();
    return asyncApiRun(new ApiGetProcessing({ serviceId: v }));
  }, [asyncApiRun, resetAdditional]);

  const handleChangeCategory = useCallback((v: string) => {
    prepareProcessingData(v)
    .then((res) => setProcessingList(res.responceData))
    .catch((e) => {
      console.log(e);
      if (e.error.errorCode === '404') {
        setProcessingList([]);
      } else {
        pushMessage('エラー', ['商品情報の取得に失敗しました。']);
      }
    });
  }, [
    prepareProcessingData,
    pushMessage,
  ]);

  const handleChangeProcessing = useCallback((v: string) => {
    setProducts([]);
    setProduct('')
    setProcessing(v);
    setItemSetting(null);
    resetAdditional();
  }, [resetAdditional]);

  const handleChangeWord = useCallback((v: string) => {
    setWord(v);
  }, []);

  const searchProducts = useCallback(async(categoryId: string, printProcessId?: string) => {
    const _processing = printProcessId ?? processing;
    return asyncApiRun(new ApiGetProducts({
      keyword: word,
      printProcessId: _processing,
      serviceId: categoryId,
    }));
  }, [
    asyncApiRun,
    word,
    processing,
  ]);
  const getElements = useCallback(async(itemId: string) => {
    try {
      const result = await asyncApiRun(new ApiGetElements({ itemId }));
      // console.log(result);
      return result.elements;
    } catch(e: any) {
      if (e?.error?.errorCode === '404') {
        return [];
      } else {
        return Promise.reject([]);
      }
    }
  }, [asyncApiRun]);
  const getAdditionalInfo = useCallback(async(productNameId: string) => {
    try {
      const result = await asyncApiRun(new ApiEndpaper({ productNameId, parentFlg: '1' }));
      // console.log(result);
      setAdditionalInfoList(result.additionalInfos)
      return result.additionalInfos;
    } catch(e: any) {
      if (e?.error?.errorCode === '404') {
        return [];
      } else {
        return Promise.reject('');
      }
    }
  }, [asyncApiRun]);
  const getAdditionalInfo2 = useCallback(async(productNameId: string) => {
    try {
      const result = await asyncApiRun(new ApiEndpaper2({ productNameId, parentFlg: '1' }));
      setAdditionalInfoList2(result.additionalInfos)
      // console.log(result);
      return result.additionalInfos
    } catch(e: any) {
      if (e?.error?.errorCode === '404') {
        return [];
      } else {
        return Promise.reject('');
      }
    }
  }, [asyncApiRun]);

  const onClickSearch = useCallback(async(categoryId?: string) => {
    const _category = (() => {
      const id = (categoryId ?? category);
      const target = categoryOptions.find((v) => {
        if (v.value === id) {
          return true;
        }
        return !!v.value.split(',').find(v2 => v2 === id);
      });
      return target?.value ?? id;
    })();
    if (category !== _category) setCategory(_category);
    if (!_category) {
      pushMessage(
        '確認',
        ['カテゴリが選択されていません。']
      )
      return;
    }
    searchProducts(_category)
    .then((res) => {
      setProducts(res.products);
    })
    .catch((e) => {
      if (e?.error?.errorCode === '404') {
        pushMessage('エラー', ['該当するデータが存在しません。']);
        return;
      }
      pushMessage('エラー', ['商品情報の取得に失敗しました。']);
    });
  }, [
    category,
    searchProducts,
    pushMessage,
    categoryOptions
  ]);

  const prepareProductsDetail = useCallback((id: string, categoryId: string) => {
    setItemSetting(null);
    setItemElements([]);
    setProductDetail(null);
    setProduct(id);
    return asyncApiRun(new ApiProductDetail({
      serviceId: categoryId,
      productNameId: id,
      parentFlg: '1',
    }));
  }, [
    asyncApiRun,
  ]);



  const getItemSettingsAndElements = useCallback((itemId: string, productNameId: string) => {
    return Promise.all([
      asyncApiRun(new ApiGetItemSettings({ itemId })),
      getElements(itemId),
      getAdditionalInfo(productNameId),
      getAdditionalInfo2(productNameId),
    ]);
  }, [
    asyncApiRun,
    getAdditionalInfo,
    getAdditionalInfo2,
  ]);

  const onSelectProduct = useCallback((id: string) => {
    if (id === product) return;
    prepareProductsDetail(id, category).then((res) => {
      const itemId = res.item[0].itemId ?? '';
      const item = res.item[0];
      if (!item) {
        pushMessage('エラー', ['商品情報の取得に失敗しました。']);
        return;
      }
      setProductDetail(res);
      console.log(res.productNameId);
      getItemSettingsAndElements(itemId, res.productNameId).then(([
        { settings },
        elements,
        tape,
        mat,
      ]) => {
        setItemSetting(settings);
        const paperList = elements.find((v) => v.progFlg === 'paper');
        const borderList = elements.find((v) => v.progFlg === 'edgeDtemp');
        const lamiList = elements.find((v) => v.progFlg === 'laminate');
        if (paperList?.list.length === 1) setPaperType(paperList.list[0].dataId);
        if (borderList?.list.length === 1) setBorderType(borderList.list[0].dataId);
        if (lamiList?.list.length === 1) setLaminateType(lamiList.list[0].dataId);
        if (tape.length === 1) setAdditionalInfo(tape[0].additionalInfoId);
        if (mat.length === 1) setAdditionalInfo2(mat[0].additionalInfo2Id);
        setItemElements(elements);

      })
    }).catch(() => pushMessage('エラー', ['商品情報の取得に失敗しました。']));
  }, [
    product,
    category,
    prepareProductsDetail,
    getItemSettingsAndElements,
    pushMessage,
  ]);

  const handleChangeSetData = useCallback((param: PrintOrderRequestParam, fromFavorite?: boolean) => {
    const {
      paperType: _paperType,
      borderType: _borderType,
      laminateType: _laminateType,
      layoutInitPos: _layoutInitPos,
      layoutMargin: _layoutMargin,
      trimming: _trimming,
      backPrint: _backPrint,
      printDate: _printDate,
      additionalInfo: _additionalInfo,
      additionalInfo2: _additionalInfo2,
    } = param;
    if (_paperType !== undefined) setPaperType(_paperType);
    if (_borderType !== undefined) setBorderType(_borderType);
    if (_laminateType !== undefined) setLaminateType(_laminateType);
    if (_layoutInitPos !== undefined) {
      if (layoutInitPos === 1 && _layoutInitPos !== 1 && !fromFavorite) {
        dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            '画像の初期配置方法が「内接」か「ピクセル等倍」の場合は、',
            '初期配置時に余白が出る可能性があります。',
            'そのため、「レイアウト時の余白」の許可設定が強制的にONになりますが、',
            'よろしいですか？',
          ],
          buttons: [
            { label: 'キャンセル', callback: () => dispatch(dialogActions.pop())},
            { label: 'OK', callback: () => {
              dispatch(dialogActions.pop());
              setLayoutInitPos(Number(_layoutInitPos));
              setLayoutMargin(true);
            }},
          ]
        }))
      } else {
        setLayoutInitPos(Number(_layoutInitPos));
      }
    };
    if (_layoutMargin !== undefined) setLayoutMargin(_layoutMargin);
    if (_trimming !== undefined) setTrimming(_trimming);
    if (_backPrint !== undefined) setBackPrint(_backPrint);
    if (_printDate !== undefined) setPrintDate(_printDate);
    if (_additionalInfo !== undefined) setAdditionalInfo(_additionalInfo);
    if (_additionalInfo2 !== undefined) setAdditionalInfo2(_additionalInfo2);
  }, [
    layoutInitPos,
    dispatch,
  ]);

  const loadFavoriteItem = useCallback(() => {
    dispatch(dialogActions.push({
      title: 'お気に入り商品設定',
      element: <FavoriteBrandConfigForPrint
        kijshopCd={kijshopCd}
        okButtonCallback={(id) => {
          loadingHooks.setLoadingMessage('データを読み込み中です...');
          dispatch(apiActions.run(
            new ApiPrintFavoriteProductDataGet({ kijshopCd, favProductId: id}),
            {
              onSuccess: async (res: ResponseBase<PrintFavoriteProductData>) => {
                if (res.body.data) {
                  dispatch(dialogActions.pop());
                  setStatus('info');
                  const productData: PrintProductDataEntity = JSON.parse(res.body.data?.jsonList[0].body);
                  const _categoryId = productData.serviceId;
                  const categoryId = categoryOptions.find((v) => {
                    if (v.value === _categoryId) {
                      return true;
                    }
                    return !!v.value.split(',').find(v2 => v2 === _categoryId);
                  })?.value ?? _categoryId;
                  try {
                    const ProcessingRes = await prepareProcessingData(categoryId);
                    setProcessingList(ProcessingRes.responceData);
                    setCategory(categoryId);
                    const productsRes = await searchProducts(categoryId);
                    setProducts(productsRes.products);
                    const productsDetailRes = await prepareProductsDetail(productData.product.productNameId, categoryId);
                    setProductDetail(productsDetailRes);
                    const itemId = productsDetailRes.item[0].itemId ?? '';
                    const [{ settings }, elements, tape, mat] = await getItemSettingsAndElements(itemId, productsDetailRes.productNameId);
                    setItemSetting(settings);
                    setItemElements(elements);
                    setAdditionalInfoList(tape);
                    setAdditionalInfoList2(mat);
                    if (productData.meta) {
                      const {
                        paperType,
                        borderType,
                        laminateType,
                        trimBaseType,
                        datePrint,
                        layoutMargin,
                        autoTrim,
                        backPrint,
                        additionalInfo,
                        additionalInfo2,
                      } = productData.meta;
                      handleChangeSetData({
                        paperType,
                        borderType,
                        laminateType,
                        layoutInitPos: trimBaseType,
                        printDate: datePrint,
                        layoutMargin,
                        trimming: autoTrim,
                        backPrint,
                        additionalInfo,
                        additionalInfo2,
                      }, true);
                    }
                  } catch (error) {
                    setStatus('select');
                    pushMessage('エラー', ['データの読み込みに失敗しました']);
                  }
                }
                loadingHooks.setLoadingMessage('');
              },
              onError: () => {
                loadingHooks.setLoadingMessage('');
                pushMessage('エラー', ['データの読み込みに失敗しました']);
              }
            }
          ))
        }}
      />,
      className: 'favorite_dialog',
    }))
  }, [
    dispatch,
    prepareProcessingData,
    searchProducts,
    prepareProductsDetail,
    getItemSettingsAndElements,
    handleChangeSetData,
    loadingHooks,
    pushMessage,
    categoryOptions
  ]);

  const onClickNext = useCallback(() => {
    getSelectItem().then(() => {
      setStatus('info');
    }).catch((e) => {
      if (Array.isArray(e)) {
        pushMessage(
          '確認',
          e,
        );
      } else {
        pushMessage(
          'エラー',
          ['エラーが発生しました。'],
        );
      }
    })
  }, [
    pushMessage,
    getSelectItem,
  ]);

  const getTemplate = useCallback((param: { itemId: string, borderType?: string
}) => {
    const { itemId, borderType } = param;
    return new Promise<PrintTemplateDataEntity[]>((resolve, reject) => {

      dispatch(apiActions.run(new ApiGetTemplates({itemId, edgeDtempId: borderType}), {
        onSuccess: (res) => {
          if (res.error.errorCode === '404') {
            reject('対象のテンプレートが見つかりませんでした');
            return;
          }
          const data = res.body.data as TemplatesResponse
          const {
            templateGroupData,
          } = data;
          const target = data.templateGroupData[0];
          const BASE_URL = `${Env.api.back.protocol}://${Env.api.back.host}/templates`;

          if (!templateGroupData) {
            console.error('templateGroupData not found !!!!');
            reject('error');
            return;
          };
          return Promise.all(
            templateGroupData.map((v) => fetch(
              `${BASE_URL}/${v.hpsTppath}/template.xml`,
              { method: 'GET', mode: 'cors' },
            ).then(async(res) => ({
              template: await res.text(),
              ...v,
            }))),
          )
          .then(async (parses) => {
            const temp = await Promise.all(parses.map(async(data) => ({
              ...data,
              template: await new XmlParser().parse<TrimTempModel>(data.template),
            })))

            resolve(temp);
          });
        },
      }));
    })
  }, [dispatch]);

  const onClickSave = useCallback(async() => {
    const isLami = !!itemElements.find((v) => v.progFlg === 'laminate')?.list?.length;
    const isPaper = !!itemElements.find((v) => v.progFlg === 'paper')?.list?.length;
    const isEdge = !!itemElements.find((v) => v.progFlg === 'edgeDtemp')?.list?.length;
    const validateResult: string[] = [];
    if (isPaper && !paperType) validateResult.push('ペーパー種が設定されていません。');
    if (isLami && !laminateType) validateResult.push('ラミネート種を選択してください。');
    if (isEdge && !borderType) validateResult.push('フチが設定されていません。');
    if (additionalInfoList.length && !additionalInfo) validateResult.push('テープ色が設定されていません。');
    if (additionalInfoList2.length && !additionalInfo2) validateResult.push('マットが設定されていません。');

    if (validateResult.length) {
      pushMessage(
        '確認',
        validateResult,
      );
      return;
    }
    if (!currentData) return;
    await lockActions.check();
    // loadingHooks.setLoadingMessage('注文情報を保存中です...');
    try {
      if (
        !productItemDetail
        || !itemElements
        || !itemSetting
      ) return;
      const targetCategory = categories.find((v) => v.serviceId === category)
      const item = currentData?.meta.createItem();
      const productData = await getSelectItem();
      item.productData = {
        serviceId: targetCategory?.serviceId ?? '',
        serviceName: targetCategory?.serviceName ?? '',
        ProductDetailItemData: productItemDetail,
        productDetailParent: productData.productDetail,
        product: productData.product,
        itemElements,
        itemSetting,
        additional: {
          info1: additionalInfoList,
          info2: additionalInfoList2,
        },
      };
      item.productData.meta = {
        trimBaseType: (layoutInitPos as 1 | 2 | 3) || INIT_PRINT_SHOP_SETTING.layoutPos,
        paperType,
        borderType,
        laminateType,
        layoutMargin: itemSetting.space ? layoutMargin : false,
        backPrint: itemSetting.backprint ? backPrint : false,
        autoTrim: itemSetting.trimming ? trimming : false,
        datePrint: printDate,
        additionalInfo,
        additionalInfo2,
      };
      try {
        const templateData = await getTemplate({ itemId: productItemDetail.itemId, borderType: borderType || undefined });
        item.templates = templateData;
      } catch(e) {
        pushMessage('エラー', ['テンプレート情報の取得に失敗しました。']);
        return;
      }
      dispatch(printActions.shopOrder({ kijshopCd, shopOrderId }).item().create(
        {
          ...item,
        },
        {
          success: async() => {
            await changeStatus('created');
            loadingHooks.setLoadingMessage('');
            dispatch(push(`${RoutingPath.cnv.printOrderSelectImage({ kijshopCd, shopOrderId })}?t=${item.id}`));
          }
        }
      ))
    } catch(e) {

    }

  }, [
    paperType,
    borderType,
    laminateType,
    layoutInitPos,
    layoutMargin,
    trimming,
    backPrint,
    pushMessage,
    dispatch,
    categories,
    currentData,
    getSelectItem,
    getTemplate,
    category,
    itemElements,
    itemSetting,
    kijshopCd,
    loadingHooks,
    productItemDetail,
    shopOrderId,
    printDate,
    lockActions,
    changeStatus,
    additionalInfo,
    additionalInfo2,
    additionalInfoList,
    additionalInfoList2,
  ]);

  const onClickReturnItemSelect = useCallback(() => {
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '商品選択画面へ移動しようとしています。',
        '入力した商品情報はクリアされますが、よろしいですか？',
      ],
      buttons: [
        { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
        { label: 'OK', callback: () => {
          dispatch(dialogActions.pop());
          resetInfoData();
          setStatus('select');
        } }
      ]
    }))
  }, [resetInfoData]);

  const handlerClickFinish = useCallback(() => {
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: ['保存していない内容は失われます。終了してもよろしいですか？'],
      buttons: [
        {
          label: 'いいえ',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        },
        {
          label: 'はい',
          callback: () => {
            dispatch(dialogActions.pop());
            dispatch(push(finishPath));
          },
        },
      ],
    }));
  }, [
    dispatch,
    finishPath,
  ]);


  /* ---- Effect ---- */
  useEffect(() => {
    const initCategory = PrintSizeChange.category;
    setIsSizeAdd(!!initCategory);
    asyncApiRun(new ApiCategories({ type: 'matomete' }))
      .then((res) => {
        setCategories(res?.service ?? []);
        if (initCategory) {
          onClickSearch(initCategory);
        }
      });
    getData();
    return () => {
      dispatch(printActions.setIsNextRouteSelectImage(false));
    };
  }, []);

  useEffect(() => {
    setLayoutInitPos(shopMeta.printSetting?.layoutPos ?? INIT_PRINT_SHOP_SETTING.layoutPos);
    setLayoutMargin(shopMeta.printSetting?.layoutMargin ?? INIT_PRINT_SHOP_SETTING.layoutMargin);
    setTrimming(shopMeta.printSetting?.autoTrimming ?? INIT_PRINT_SHOP_SETTING.autoTrimming);
    setBackPrint(shopMeta.printSetting?.backPrint ?? INIT_PRINT_SHOP_SETTING.backPrint);
  }, [shopMeta]);

  return {
    categories: {
      list: categoryOptions,
      value: category,
      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => handleChangeCategory(e.target.value),
    },
    processing: {
      list: processingOptions,
      value: processing,
      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => handleChangeProcessing(e.target.value),
    },
    word: {
      value: word,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChangeWord(e.target.value),
    },
    search: {
      onClick: () => onClickSearch(),
    },
    printItems: {
      selectId: product,
      productList: products,
      callback: onSelectProduct,
      hidden: !products.length,
    },
    itemSetting: {
      data: itemSetting,
      request: {
        paperType,
        borderType,
        additionalInfo,
        additionalInfo2,
        laminateType,
        layoutInitPos,
        layoutMargin,
        trimming,
        backPrint,
        printDate,
      },
      callback: handleChangeSetData,
      itemElements,
    },
    additionalList: {
      tape: additionalInfoList,
      mat: additionalInfoList2,
    },
    onClickSave,
    onClickNext,
    onClickReturnItemSelect,
    handlerClickFinish,
    status,
    loadFavoriteItem,
    LoadingPopup:  loadingHooks.LoadingPopup,
    isSizeAdd,
  }
}
